<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import {
  genderOptions,
  marriageRegimeOptions,
  maritalStatusOptions,
} from '../composables/salesPartyFormOptions';
import SalesDetailFactList from './SalesDetailFactList.vue';

const props = defineProps({
  party: { type: Object, required: true },
  summary: { type: Boolean, default: false },
  cardTitle: { type: String, default: '' },
});

const { t } = useI18n();
const identity = computed(() => props.party.profile?.identity || {});
const optionLabel = (options, value) =>
  options.find(option => option.value === String(value))?.label || value;
const representativeLabels = computed(() =>
  [
    identity.value.is_legal_representative
      ? t('SALES.DETAIL.PARTY_FIELDS.LEGAL_REPRESENTATIVE')
      : null,
    identity.value.is_partner ? t('SALES.DETAIL.PARTY_FIELDS.PARTNER') : null,
  ].filter(Boolean)
);
const detailItems = computed(() => {
  const values = identity.value;
  const spouseItems =
    values.marital_status === '1'
      ? [
          {
            key: 'marital-property-regime',
            label: t('SALES_PARTIES.FORM.MARRIAGE_REGIME'),
            value: optionLabel(
              marriageRegimeOptions(t),
              values.marital_property_regime
            ),
          },
          {
            key: 'spouse-name',
            label: t('SALES_PARTIES.FORM.SPOUSE_NAME'),
            value: values.spouse_name,
          },
          {
            key: 'spouse-cpf',
            label: t('SALES_PARTIES.FORM.SPOUSE_CPF'),
            value: values.spouse_cpf,
          },
        ]
      : [];

  return [
    { key: 'cpf', label: t('SALES_PARTIES.FORM.CPF'), value: values.cpf },
    {
      key: 'birth-date',
      label: t('SALES_PARTIES.FORM.BIRTH_DATE'),
      value: values.birth_date,
    },
    {
      key: 'gender',
      label: t('SALES_PARTIES.FORM.GENDER'),
      value: optionLabel(genderOptions(t), values.gender),
    },
    { key: 'name', label: t('SALES_PARTIES.FORM.NAME'), value: values.name },
    {
      key: 'mother-name',
      label: t('SALES_PARTIES.FORM.MOTHER_NAME'),
      value: values.mother_name,
    },
    {
      key: 'father-name',
      label: t('SALES_PARTIES.FORM.FATHER_NAME'),
      value: values.father_name,
    },
    {
      key: 'nationality',
      label: t('SALES_PARTIES.FORM.NATIONALITY'),
      value: values.nationality,
    },
    {
      key: 'phone',
      label: t('SALES_PARTIES.FORM.PHONE'),
      value: values.phone,
    },
    {
      key: 'profession',
      label: t('SALES_PARTIES.FORM.PROFESSION'),
      value: values.profession,
    },
    {
      key: 'email',
      label: t('SALES_PARTIES.FORM.EMAIL'),
      value: values.email || props.party.subject?.email,
    },
    { key: 'rg', label: t('SALES_PARTIES.FORM.RG'), value: values.rg },
    {
      key: 'rg-issuer',
      label: t('SALES_PARTIES.FORM.RG_ISSUER'),
      value: values.rg_issuer,
    },
    {
      key: 'rg-issued-on',
      label: t('SALES_PARTIES.FORM.RG_ISSUED_ON'),
      value: values.rg_issued_on,
    },
    {
      key: 'marital-status',
      label: t('SALES_PARTIES.FORM.MARITAL_STATUS'),
      value: optionLabel(maritalStatusOptions(t), values.marital_status),
    },
    ...spouseItems,
  ];
});
const summaryItems = computed(() => {
  const values = identity.value;
  const items = [
    { key: 'cpf', label: t('SALES_PARTIES.FORM.CPF'), value: values.cpf },
    {
      key: 'phone',
      label: t('SALES_PARTIES.SUMMARY.PHONE'),
      value: values.phone,
    },
    { key: 'name', label: t('SALES_PARTIES.FORM.NAME'), value: values.name },
    {
      key: 'gender',
      label: t('SALES_PARTIES.FORM.GENDER'),
      value: optionLabel(genderOptions(t), values.gender),
    },
    {
      key: 'birth-date',
      label: t('SALES_PARTIES.FORM.BIRTH_DATE'),
      value: values.birth_date?.toString().split(' ')[0],
    },
    {
      key: 'nationality',
      label: t('SALES_PARTIES.FORM.NATIONALITY'),
      value: values.nationality,
    },
    {
      key: 'mother-name',
      label: t('SALES_PARTIES.FORM.MOTHER_NAME'),
      value: values.mother_name,
    },
    {
      key: 'father-name',
      label: t('SALES_PARTIES.FORM.FATHER_NAME'),
      value: values.father_name || '---',
    },
    {
      key: 'marital-status',
      label: t('SALES_PARTIES.FORM.MARITAL_STATUS'),
      value: optionLabel(maritalStatusOptions(t), values.marital_status),
    },
    {
      key: 'stable-union',
      label: t('SALES_PARTIES.SUMMARY.STABLE_UNION'),
      value:
        values.stable_union === true ||
        values.stable_union === 'S' ||
        values.stable_union === '1'
          ? t('SALES_PARTIES.SUMMARY.YES')
          : t('SALES_PARTIES.SUMMARY.NO'),
    },
  ];

  if (values.marital_property_regime || values.marital_property_regime_label) {
    items.push({
      key: 'marital-property-regime',
      label: t('SALES_PARTIES.FORM.MARRIAGE_REGIME'),
      value:
        values.marital_property_regime_label ||
        optionLabel(marriageRegimeOptions(t), values.marital_property_regime),
    });
  }
  if (values.spouse_name) {
    items.push({
      key: 'spouse-name',
      label: t('SALES_PARTIES.SUMMARY.SPOUSE'),
      value: values.spouse_name,
    });
  }
  items.push(
    {
      key: 'profession',
      label: t('SALES_PARTIES.FORM.PROFESSION'),
      value: values.profession || '---',
    },
    {
      key: 'email',
      label: t('SALES_PARTIES.SUMMARY.EMAIL'),
      value: values.email || props.party.subject?.email || '---',
    }
  );
  if (values.rg) {
    items.push(
      { key: 'rg', label: t('SALES_PARTIES.FORM.RG'), value: values.rg },
      {
        key: 'rg-issuer',
        label: t('SALES_PARTIES.SUMMARY.RG_ISSUER'),
        value: values.rg_issuer,
      },
      {
        key: 'rg-issued-on',
        label: t('SALES_PARTIES.FORM.RG_ISSUED_ON'),
        value: values.rg_issued_on,
      }
    );
  }
  return items;
});
const items = computed(() =>
  props.summary ? summaryItems.value : detailItems.value
);
const title = computed(
  () => props.cardTitle || t('SALES_PARTIES.PERSONAL.TITLE')
);
</script>

<template>
  <LaisCard :title="title" prefix-icon="i-lucide-user-round">
    <div class="grid gap-4">
      <div v-if="!summary" class="flex flex-wrap gap-2">
        <span
          class="rounded-full bg-lais-bg-soft px-2 py-1 text-xs font-semibold text-lais-text-muted"
        >
          {{ t('SALES.DETAIL.PERSON_KIND.PERSON') }}
        </span>
        <span
          v-for="label in representativeLabels"
          :key="label"
          class="rounded-full bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-800"
        >
          {{ label }}
        </span>
      </div>
      <SalesDetailFactList :items="items" />
    </div>
  </LaisCard>
</template>
